import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import useBus from 'use-bus'
import { CompanyContext } from './CompanyContext'
import { BULK_PROGRESS_READY_EVENT } from './BulkProgressContext'
import { Notification, toast } from 'components/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from 'api/http'
import { queryKeys } from 'api/queryKeys'

/**
 * Scheduled post groups for the active company.
 *
 * Data lives in React Query (key per `companyId` so cache splits cleanly on
 * company switch). The context still exposes the legacy shape consumed by
 * ~10 files — `scheduledPosts`, `getScheduledPostGroup`, `triggerUpdate`, etc —
 * but reads/writes now go through the cache so revisiting routes is free.
 */

const SCHEDULED_INCLUDE = [
    'ruleGroup',
    'tags',
    'posts',
    'posts.assets',
    'posts.assets.image',
    'posts.assets.image.thumbnail',
    'posts.assets.video',
    'posts.assets.video.thumbnail',
    'posts.cover',
    'user',
    'user.image',
]
const SCHEDULED_INCLUDE_STR = SCHEDULED_INCLUDE.join(',')

const scheduledListKey = (companyId) => [
    'postGroups',
    'scheduledAll',
    String(companyId ?? ''),
]
const scheduledFilteredKey = (companyId, filters, sort, getAll) => [
    'postGroups',
    'scheduledFiltered',
    String(companyId ?? ''),
    JSON.stringify(filters ?? {}),
    sort || '-publish_at',
    !!getAll,
]
const scheduledDetailKey = (postGroupId) =>
    queryKeys.postGroups.detail(postGroupId, { include: 'scheduled' })

const ScheduledPostContext = React.createContext({
    scheduledPosts: [],
    isLoadingScheduledPosts: false,
    triggerUpdatePostGroups: undefined,
    triggerUpdate: () => {},
    updateScheduledPosts: () => {},
    getScheduledPostGroup: () => {},
    modifyScheduledPost: () => {},
    findScheduledPostByID: () => {},
    getScheduledPostGroups: () => {},
    setScheduledPostGroupFieldValue: () => {},
})

async function fetchAllPostGroupsForCompany(companyId, signal) {
    let all = []
    let nextUrl
    do {
        const url = nextUrl || `/companies/${companyId}/postGroups`
        const params = nextUrl
            ? undefined
            : { include: SCHEDULED_INCLUDE_STR, sort: '-publish_at' }
        const response = await api.get(url, params, { signal })
        all = all.concat(response?.data ?? [])
        nextUrl = response?.links?.next || undefined
    } while (nextUrl)
    return all
}

async function fetchPostGroupsWithFilters(
    companyId,
    filters,
    sort,
    getAll,
    signal
) {
    if (!companyId) return { postGroups: [], links: undefined }
    const baseParams = {
        include: SCHEDULED_INCLUDE_STR,
        sort: sort || '-publish_at',
        ...(getAll ? { paginate: 'false' } : {}),
        ...Object.fromEntries(
            Object.entries(filters || {}).map(([k, v]) => [`filter[${k}]`, v])
        ),
    }
    let postGroups = []
    let links
    let nextUrl
    do {
        const url = nextUrl || `/companies/${companyId}/postGroups`
        const response = await api.get(
            url,
            nextUrl ? undefined : baseParams,
            { signal }
        )
        postGroups = postGroups.concat(response?.data ?? [])
        links = response?.links
        nextUrl = getAll && links?.next ? links.next : undefined
    } while (getAll && nextUrl)
    return { postGroups, links }
}

const ScheduledPostProvider = ({ children }) => {
    const { company } = useContext(CompanyContext)
    const companyId = company?.id
    const qc = useQueryClient()
    // Bumped on every triggerUpdate so legacy consumers that depend on
    // `triggerUpdatePostGroups` as a useEffect signal keep working. RQ
    // consumers don't need it — they auto-refetch on invalidation.
    const [triggerUpdatePostGroups, setTriggerUpdatePostGroups] = useState()
    const prevPostsRef = useRef()

    const scheduledQuery = useQuery({
        queryKey: scheduledListKey(companyId),
        enabled: !!companyId,
        staleTime: 60_000,
        queryFn: ({ signal }) =>
            fetchAllPostGroupsForCompany(companyId, signal),
    })

    const scheduledPosts = scheduledQuery.data
    const isLoadingScheduledPosts = scheduledQuery.isLoading

    // Status-change toasts (error/published) — fire when the cached list
    // transitions between snapshots, matching legacy `sendStatusChangeNotifications`.
    useEffect(() => {
        const oldPosts = prevPostsRef.current
        const newPosts = scheduledPosts
        prevPostsRef.current = newPosts
        if (!oldPosts || !newPosts) return
        const findPost = (postId, source) => {
            let found
            source?.forEach((pg) => {
                pg?.posts?.forEach((p) => {
                    if (p?.id == postId) found = p
                })
            })
            return found
        }
        oldPosts.forEach((oldPg) => {
            oldPg?.posts?.forEach((oldPost) => {
                const newPost = findPost(oldPost?.id, newPosts)
                if (
                    oldPost?.status !== 'error' &&
                    newPost?.status === 'error'
                ) {
                    toast.push(
                        <Notification
                            type="danger"
                            title={`There was an error while publishing the post: ${newPost.status_message}`}
                        />
                    )
                }
                if (
                    oldPost?.status !== 'published' &&
                    newPost?.status === 'published'
                ) {
                    toast.push(
                        <Notification
                            type="success"
                            title={`The post was published successfully`}
                        />
                    )
                }
            })
        })
    }, [scheduledPosts])

    const setScheduledPostGroupFieldValue = useCallback(
        (post_group_id, field, value) => {
            const key = scheduledListKey(companyId)
            qc.setQueryData(key, (prev) => {
                if (!Array.isArray(prev)) return prev
                const idx = prev.findIndex((e) => e?.id == post_group_id)
                if (idx < 0) return prev
                const next = prev.slice()
                next[idx] = { ...next[idx], [field]: value }
                return next
            })
            qc.setQueryData(
                scheduledDetailKey(post_group_id),
                (prev) => (prev ? { ...prev, [field]: value } : prev)
            )
        },
        [qc, companyId]
    )

    const getScheduledPostGroup = useCallback(
        async (post_group_id) => {
            return qc.fetchQuery({
                queryKey: scheduledDetailKey(post_group_id),
                // Always revalidate: post saves go through imperative Api.put/post
                // calls that never invalidate the RQ cache, so a non-zero staleTime
                // would make refreshFromDb serve the pre-save snapshot (dropping
                // freshly added media). Matches the pre-React-Query always-fresh fetch.
                staleTime: 0,
                queryFn: async ({ signal }) => {
                    const r = await api.get(
                        `/postGroups/${post_group_id}`,
                        {
                            include: SCHEDULED_INCLUDE_STR,
                            sort: '-publish_at',
                        },
                        { signal }
                    )
                    return r?.data
                },
            })
        },
        [qc]
    )

    const getScheduledPostGroups = useCallback(
        async (
            filters = {},
            sort = '-publish_at',
            getAll = false,
            returnLinks = false
        ) => {
            if (!companyId) {
                return returnLinks
                    ? { postGroups: [], links: undefined }
                    : []
            }
            const result = await qc.fetchQuery({
                queryKey: scheduledFilteredKey(
                    companyId,
                    filters,
                    sort,
                    getAll
                ),
                staleTime: 30_000,
                queryFn: ({ signal }) =>
                    fetchPostGroupsWithFilters(
                        companyId,
                        filters,
                        sort,
                        getAll,
                        signal
                    ),
            })
            return returnLinks ? result : result.postGroups
        },
        [qc, companyId]
    )

    const findScheduledPostByID = useCallback(
        (post_id, _posts = scheduledPosts) => {
            let returnPost
            _posts?.forEach((postGroup) => {
                postGroup?.posts?.forEach((post) => {
                    if (post?.id == post_id) returnPost = post
                })
            })
            return returnPost
        },
        [scheduledPosts]
    )

    const modifyScheduledPost = useCallback(
        (post_id, obj) => {
            let next
            qc.setQueryData(scheduledListKey(companyId), (prev) => {
                if (!Array.isArray(prev)) return prev
                next = prev.map((postGroup) => ({
                    ...postGroup,
                    posts: postGroup?.posts?.map((post) =>
                        post?.id == post_id ? { ...post, ...obj } : post
                    ),
                }))
                return next
            })
            return next
        },
        [qc, companyId]
    )

    const updateScheduledPosts = useCallback(async () => {
        if (!companyId) return
        await qc.invalidateQueries({ queryKey: scheduledListKey(companyId) })
    }, [qc, companyId])

    const triggerUpdate = useCallback(() => {
        setTriggerUpdatePostGroups(Math.random())
        qc.invalidateQueries({ queryKey: queryKeys.postGroups.all })
    }, [qc])

    useBus(
        BULK_PROGRESS_READY_EVENT,
        () => {
            triggerUpdate()
        },
        [triggerUpdate]
    )

    const value = useMemo(
        () => ({
            scheduledPosts,
            isLoadingScheduledPosts,
            updateScheduledPosts,
            getScheduledPostGroup,
            modifyScheduledPost,
            findScheduledPostByID,
            getScheduledPostGroups,
            triggerUpdatePostGroups,
            setScheduledPostGroupFieldValue,
            triggerUpdate,
        }),
        [
            scheduledPosts,
            isLoadingScheduledPosts,
            updateScheduledPosts,
            getScheduledPostGroup,
            modifyScheduledPost,
            findScheduledPostByID,
            getScheduledPostGroups,
            triggerUpdatePostGroups,
            setScheduledPostGroupFieldValue,
            triggerUpdate,
        ]
    )

    return (
        <ScheduledPostContext.Provider value={value}>
            {children}
        </ScheduledPostContext.Provider>
    )
}

export { ScheduledPostContext, ScheduledPostProvider }
